import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, Trash2, FileText, Check, X, AlertTriangle } from "lucide-react";

const CATEGORIES = [
  "Founder Documents", "Company Documents", "Financial Documents", "Address Proof",
  "Identity Documents", "Tax Documents", "Licenses", "Agreements", "Certificates",
  "Branding Assets", "Product Documents", "Technical Documents", "Other",
];

const STATUS_CLS = {
  Required: "border-[#FF8800]/40 text-[#FF8800]",
  Uploaded: "border-[#00B0FF]/40 text-[#00B0FF]",
  "Under Review": "border-[#00B0FF]/40 text-[#00B0FF]",
  Accepted: "border-[#00E676]/40 text-[#00E676]",
  Rejected: "border-red-500/40 text-red-400",
  "Replacement Required": "border-[#FF8800]/40 text-[#FF8800]",
  Expired: "border-red-500/40 text-red-400",
};

export function DocVault({ startupId, canReview = false, testid = "docvault" }) {
  const [docs, setDocs] = useState([]);
  const [requests, setRequests] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ name: "", category: "Founder Documents", expiry_date: "", data: "", request_id: "" });

  const load = useCallback(() => {
    api.get(`/portal/documents?startup_id=${startupId}`).then((r) => setDocs(r.data)).catch(() => {});
    api.get(`/portal/doc-requests?startup_id=${startupId}`).then((r) => setRequests(r.data)).catch(() => {});
  }, [startupId]);

  useEffect(() => { load(); }, [load]);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["application/pdf", "image/png", "image/jpeg", "image/webp"].includes(file.type)) {
      toast.error("Only PDF or image files");
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      toast.error("File must be under 4MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, data: reader.result, name: f.name || file.name.replace(/\.[^.]+$/, "") }));
    reader.readAsDataURL(file);
  };

  const upload = async (e) => {
    e.preventDefault();
    if (!form.data) {
      toast.error("Choose a file first");
      return;
    }
    setUploading(true);
    try {
      await api.post("/portal/documents", { ...form, startup_id: startupId });
      toast.success("Document uploaded to your vault");
      setForm({ name: "", category: "Founder Documents", expiry_date: "", data: "", request_id: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const viewDoc = async (doc) => {
    try {
      const res = await api.get(`/portal/documents/${doc.id}/file`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const setStatus = async (doc, status) => {
    try {
      await api.put(`/portal/documents/${doc.id}`, { status });
      toast.success(`Marked as ${status}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (doc) => {
    if (!window.confirm(`Delete "${doc.name}"?`)) return;
    try {
      await api.delete(`/portal/documents/${doc.id}`);
      toast.success("Document deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const expiryBadge = (doc) => {
    if (!doc.expiry_date) return null;
    const days = Math.ceil((new Date(doc.expiry_date) - Date.now()) / 86400000);
    if (days < 0) return <span className="text-[10px] font-mono-tech text-red-400 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> EXPIRED</span>;
    if (days <= 30) return <span className="text-[10px] font-mono-tech text-[#FF8800] flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Expires in {days}d</span>;
    return null;
  };

  const pendingRequests = requests.filter((r) => r.status === "pending");

  return (
    <div className="space-y-6" data-testid={testid}>
      {pendingRequests.length > 0 && (
        <div className="glass rounded-xl p-5 border border-[#FF8800]/30" data-testid={`${testid}-requests`}>
          <p className="font-mono-tech text-[10px] tracking-[0.3em] text-[#FF8800] uppercase mb-3">Requested by your specialist</p>
          <div className="space-y-2.5">
            {pendingRequests.map((r) => (
              <div key={r.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 rounded-lg border border-white/10 px-4 py-3" data-testid={`doc-request-${r.id}`}>
                <div>
                  <p className="text-sm font-medium text-white">{r.name} {r.required && <span className="text-[#FF8800] text-xs">(required)</span>}</p>
                  {r.reason && <p className="text-xs text-slate-400 mt-0.5">{r.reason}</p>}
                  {r.due_date && <p className="text-[10px] text-slate-500 mt-0.5 font-mono-tech">Due: {r.due_date}</p>}
                </div>
                <Button
                  size="sm"
                  data-testid={`doc-request-upload-${r.id}`}
                  onClick={() => setForm((f) => ({ ...f, name: r.name, request_id: r.id }))}
                  className="bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E] font-semibold shrink-0"
                >
                  <Upload className="w-3.5 h-3.5 mr-1.5" /> Upload Now
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <form onSubmit={upload} className="glass rounded-xl p-5 space-y-4" data-testid={`${testid}-upload-form`}>
        <p className="font-mono-tech text-[10px] tracking-[0.3em] text-[#00B0FF] uppercase">Upload to vault</p>
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label className="text-slate-300 text-xs">Document Name *</Label>
            <Input data-testid={`${testid}-name`} required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. PAN Card" className="bg-[#06080E] border-white/10" />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300 text-xs">Category</Label>
            <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
              <SelectTrigger data-testid={`${testid}-category`} className="bg-[#06080E] border-white/10 text-slate-200"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#0D111D] border-white/10 text-slate-200 max-h-56">
                {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300 text-xs">Expiry Date (optional)</Label>
            <Input data-testid={`${testid}-expiry`} type="date" value={form.expiry_date} onChange={(e) => setForm((f) => ({ ...f, expiry_date: e.target.value }))} className="bg-[#06080E] border-white/10" />
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-2 glass rounded-lg px-4 py-2.5 cursor-pointer hover:border-[#FF8800]/50 transition-colors" data-testid={`${testid}-file`}>
            <Upload className="w-4 h-4 text-[#FF8800]" />
            <span className="text-sm text-slate-300">{form.data ? "File selected" : "Choose file (PDF/image, 4MB)"}</span>
            <input type="file" accept=".pdf,image/png,image/jpeg,image/webp" className="hidden" onChange={onFile} />
          </label>
          <Button type="submit" disabled={uploading} data-testid={`${testid}-upload-submit`} className="bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E] font-semibold">
            {uploading ? "Uploading..." : form.request_id ? "Upload & Fulfil Request" : "Upload Document"}
          </Button>
        </div>
      </form>

      <div className="space-y-2.5" data-testid={`${testid}-list`}>
        {docs.length === 0 ? (
          <div className="glass rounded-xl p-10 text-center text-slate-500 text-sm">Your document vault is empty.</div>
        ) : (
          docs.map((d) => (
            <div key={d.id} className="glass rounded-xl px-4 py-3.5 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`doc-row-${d.id}`}>
              <div className="w-9 h-9 rounded-lg bg-[#00B0FF]/10 border border-[#00B0FF]/25 flex items-center justify-center shrink-0">
                <FileText className="w-4 h-4 text-[#00B0FF]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-medium text-white truncate">{d.name}</p>
                  <span className={`text-[10px] font-mono-tech px-2 py-0.5 rounded-full border ${STATUS_CLS[d.status] || STATUS_CLS.Uploaded}`}>
                    {d.status.toUpperCase()}
                  </span>
                  {expiryBadge(d)}
                </div>
                <p className="text-[11px] text-slate-500 mt-0.5">{d.category} · v{d.version} · {new Date(d.created_at).toLocaleDateString("en-IN")}</p>
                {d.notes && <p className="text-[11px] text-[#FF8800] mt-0.5">{d.notes}</p>}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button data-testid={`doc-view-${d.id}`} onClick={() => viewDoc(d)} className="text-xs text-[#00B0FF] hover:underline">View</button>
                {canReview && (
                  <>
                    <Button size="icon" variant="outline" data-testid={`doc-accept-${d.id}`} onClick={() => setStatus(d, "Accepted")} className="border-[#00E676]/30 text-[#00E676] hover:bg-[#00E676]/10 h-8 w-8" title="Accept">
                      <Check className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="icon" variant="outline" data-testid={`doc-reject-${d.id}`} onClick={() => setStatus(d, "Rejected")} className="border-red-500/30 text-red-400 hover:bg-red-500/10 h-8 w-8" title="Reject">
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </>
                )}
                {!canReview && d.mine !== false && (
                  <Button size="icon" variant="outline" data-testid={`doc-delete-${d.id}`} onClick={() => remove(d)} className="border-red-500/30 text-red-400 hover:bg-red-500/10 h-8 w-8">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
